import React from "react";

// Escape HTML special characters (except newlines which we handle separately)
function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Parse inline WhatsApp styles and URLs
function parseInlineStyles(text) {
  let escaped = escapeHtml(text);

  // Convert URLs into clickable links
  escaped = escaped.replace(
    /(https?:\/\/[^\s]+)/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>'
  );

  // WhatsApp markdown styles
  escaped = escaped
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")  // *bold*
    .replace(/\*([^*]+)\*/g, "<strong>$1</strong>")   // *bold*
    .replace(/_([^_]+)_/g, "<em>$1</em>")             // _italic_
    .replace(/~([^~]+)~/g, "<del>$1</del>")           // ~strike~
    .replace(/`([^`]+)`/g, "<code>$1</code>");        // `code`

  return escaped;
}

// Main parser: turns text into HTML, supports lists, radios, checkboxes, paragraphs, and line breaks
function parseTextToHtml(text = "") {
  if (typeof text !== "string") return "";

  // First, replace <br> or <br/> tags with \n to handle line breaks uniformly
  const cleanText = text.replace(/<br\s*\/?>/gi, "\n");

  const lines = cleanText.split(/\r?\n/);
  let html = "";
  let inList = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Radio button: - ( ) or - (x)
    const radioMatch = trimmed.match(/^-\s*\(( |x|X)\)\s+(.*)/);

    // Checkbox: - [ ] or - [x]
    const checkboxMatch = trimmed.match(/^-\s*\[( |x|X)\]\s+(.*)/);

    // Normal unordered list item: - item
    const normalListMatch = /^- /.test(trimmed);

    if (radioMatch) {
      if (!inList) {
        html += "<ul style='list-style:none; padding-left: 2.2em !important;'>";
        inList = true;
      }
      const checked = radioMatch[1].toLowerCase() === "x" ? "checked" : "";
      const content = parseInlineStyles(radioMatch[2]);
      html += `<li><input type="radio" disabled ${checked} /> ${content}</li>`;
      continue;
    }

    if (checkboxMatch) {
      if (!inList) {
        html += "<ul style='list-style:none; padding-left: 2.2em !important;'>";
        inList = true;
      }
      const checked = checkboxMatch[1].toLowerCase() === "x" ? "checked" : "";
      const content = parseInlineStyles(checkboxMatch[2]);
      html += `<li><input type="checkbox" disabled ${checked} /> ${content}</li>`;
      continue;
    }

    if (normalListMatch) {
      if (!inList) {
        html += "<ul>";
        inList = true;
      }
      const content = parseInlineStyles(trimmed.slice(2));
      html += `<li>${content}</li>`;
      continue;
    }

    // Blank line handling: close list if open, add <br> but avoid duplicates
    if (trimmed === "") {
      if (inList) {
        html += "</ul>";
        inList = false;
      }
      if (i + 1 < lines.length && lines[i + 1].trim() === "") {
        // skip multiple blank lines
        continue;
      }
      html += "<br />";
      continue;
    }

    // Close list before paragraph
    if (inList) {
      html += "</ul>";
      inList = false;
    }

    // Normal paragraph with inline styles
    html += `<p>${parseInlineStyles(trimmed)}</p>`;
  }

  // Close any open list at the end
  if (inList) {
    html += "</ul>";
  }

  return html;
}

// React component: safely render WhatsApp formatted text with line breaks & lists
function WhatsAppText({ text }) {
  const html = parseTextToHtml(text);
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

export default WhatsAppText;
